import {BaseAttribute, BaseAttributeOptions} from './BaseAttribute';
import {Enumeration} from '../../../models/data/enumeration';
import {translate} from '../../../plugins/i18n/i18n';

export type EnumerationAttributeOptions = BaseAttributeOptions & {
  enumType?: string | null;
  enumClass?: typeof Enumeration | null;
};

// Attribute for fixed set of values
export class EnumerationAttribute extends BaseAttribute {
  enumType?: string | null;
  enumClass?: typeof Enumeration | null;

  constructor(options: EnumerationAttributeOptions = {}) {
    super(options);
    this.enumType = options.enumType ?? null;
    this.enumClass = options.enumClass ?? null;
  }

  // Input type that is used if attribute is used in edit mode
  get inputType(): string {
    return 'enum';
  }

  get staticEnumValues() {
    if (!this.enumType || !this.enumClass) return undefined;
    return this.enumClass.getEnumValues(this.enumType, translate);
  }

  get customTableProperties(): Record<string, unknown> {
    return {
      enumType: this.enumType,
      // Forward enum values to generic table inputs so they can use static mode.
      staticValues: this.staticEnumValues,
    };
  }

  getInitialValue(): unknown {
    if (this.creationValue !== undefined && this.creationValue !== null) {
      return this.creationValue;
    }
    return undefined;
  }

  /**
   * Extracts the id of the enumeration value that is used in the database
   * Returns two values:
   * - The value sent to the database (the id of the enumeration value)
   * - The value to update the data in the UI (the enumeration object)
   */
  toDbValue(value: unknown): [unknown, unknown] {
    if (!value) return [null, null];
    if (typeof value === 'object' && value !== null && 'id' in value) {
      const enumValue = value as {id: unknown};
      return [enumValue.id, value];
    }
    return [null, null];
  }

  /** Converts the value from the database to a value that can be used in the UI */
  toLocalValue(item: Record<string, unknown>): unknown {
    let value = item[this.name];
    if (!value) return null;
    if (!this.enumClass || !this.enumType) return value;
    if (this.transform) {
      value = this.transform(value);
    }
    return this.enumClass.getEnumValue(this.enumType, value, translate as any);
  }
}
